import { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { FaYoutube, FaInstagram, FaTiktok, FaLinkedin, FaTwitter, FaFacebookF } from 'react-icons/fa';
import { FaAt } from 'react-icons/fa6';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage({ setUserId }) {
  const [connecting, setConnecting] = useState(null);

  const handleConnect = async (platform) => {
    setConnecting(platform);
    try {
      const response = await axios.get(`${API}/auth/${platform}/login`);
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else if (response.data.error) {
        toast.error(response.data.error, { duration: 6000 });
        setConnecting(null);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || `Failed to connect to ${platform}`;
      toast.error(errorMsg, { duration: 6000 });
      setConnecting(null);
    }
  };

  return (
    <div className="landing-page">
      <div className="hero-section">
        <div className="content-wrapper">
          <h1 className="main-title" data-testid="landing-title">
            Content Radar
          </h1>
          <p className="subtitle" data-testid="landing-subtitle">
            Create once. Post everywhere.
          </p>
          <p className="description">
            Connect YouTube, Instagram, TikTok, X (Twitter), Threads, Facebook, and LinkedIn to manage your content library and cross-post directly from one place.
          </p>

          <div className="connect-section">
            <h2 className="connect-title">Connect Your Accounts</h2>
            <div className="platform-buttons">
              <Button
                data-testid="connect-youtube-btn"
                className="platform-btn youtube-btn"
                onClick={() => handleConnect('youtube')}
                disabled={connecting !== null}
              >
                <FaYoutube className="platform-icon" />
                {connecting === 'youtube' ? 'Connecting...' : 'Connect YouTube'}
              </Button>

              <Button
                data-testid="connect-instagram-btn"
                className="platform-btn instagram-btn"
                onClick={() => handleConnect('instagram')}
                disabled={connecting !== null}
              >
                <FaInstagram className="platform-icon" />
                {connecting === 'instagram' ? 'Connecting...' : 'Connect Instagram'}
              </Button>

              <Button
                data-testid="connect-tiktok-btn"
                className="platform-btn tiktok-btn"
                onClick={() => handleConnect('tiktok')}
                disabled={connecting !== null}
              >
                <FaTiktok className="platform-icon" />
                {connecting === 'tiktok' ? 'Connecting...' : 'Connect TikTok'}
              </Button>

              <Button
                data-testid="connect-linkedin-btn"
                className="platform-btn linkedin-btn"
                onClick={() => handleConnect('linkedin')}
                disabled={connecting !== null}
              >
                <FaLinkedin className="platform-icon" />
                {connecting === 'linkedin' ? 'Connecting...' : 'Connect LinkedIn'}
              </Button>

              <Button
                data-testid="connect-twitter-btn"
                className="platform-btn twitter-btn"
                onClick={() => handleConnect('twitter')}
                disabled={connecting !== null}
              >
                <FaTwitter className="platform-icon" />
                {connecting === 'twitter' ? 'Connecting...' : 'Connect X (Twitter)'}
              </Button>

              <Button
                data-testid="connect-threads-btn"
                className="platform-btn threads-btn"
                onClick={() => handleConnect('threads')}
                disabled={connecting !== null}
              >
                <FaAt className="platform-icon" />
                {connecting === 'threads' ? 'Connecting...' : 'Connect Threads'}
              </Button>

              <Button
                data-testid="connect-facebook-btn"
                className="platform-btn facebook-btn"
                onClick={() => handleConnect('facebook')}
                disabled={connecting !== null}
              >
                <FaFacebookF className="platform-icon" />
                {connecting === 'facebook' ? 'Connecting...' : 'Connect Facebook'}
              </Button>
            </div>
          </div>

          <div className="setup-notice">
            <h3>⚙️ Setup Required</h3>
            <p>Configure OAuth credentials in <code>/app/backend/.env</code>:</p>
            <ol>
              <li><strong>YouTube:</strong> <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">Google Cloud Console</a> — Enable YouTube Data API v3</li>
              <li><strong>Instagram:</strong> <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer">Meta Developers</a> — Instagram Graph API, Business/Creator account</li>
              <li><strong>TikTok:</strong> <a href="https://developers.tiktok.com/" target="_blank" rel="noopener noreferrer">TikTok Developers</a> — Login Kit with video scopes</li>
              <li><strong>X (Twitter):</strong> <a href="https://developer.twitter.com/" target="_blank" rel="noopener noreferrer">Twitter Developer Portal</a> — Free tier (1,500 tweets/month)</li>
              <li><strong>Threads:</strong> <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer">Meta Developers</a> — Threads API (Business account required)</li>
              <li><strong>Facebook:</strong> <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer">Meta Developers</a> — Facebook Graph API for Pages</li>
              <li><strong>LinkedIn:</strong> <a href="https://developer.linkedin.com/" target="_blank" rel="noopener noreferrer">LinkedIn Developer Portal</a> — <code>w_member_social</code> scope</li>
            </ol>
          </div>

          <div className="features-section">
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Unified Dashboard</h3>
              <p>View all your content from YouTube, Instagram, and TikTok in one place</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3>Direct Cross-Posting</h3>
              <p>Post a video to multiple platforms at once — YouTube, TikTok, Instagram, LinkedIn</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔍</div>
              <h3>Duplicate Detection</h3>
              <p>Automatically identify cross-posted content using video fingerprinting</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
